import { useEffect, useState } from "react"
import { t } from "translations"

import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"
import Fab from "@mui/material/Fab"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { rewrites } from "lib/routes"

import Icon from "Components/Icon/Icon"
import Link from "Components/Link"

import {
    IContractionCounter,
    calculateContractionLength,
    calculateFrequency,
    formatCounterTime,
    formatCurrentTime,
    printResult
} from "./utils"

export default function ContractionCounter() {
    const [contractionArray, setContractionArray] = useState<
        IContractionCounter[]
    >([])

    // timer for current contraction and resting time
    const [currentContractionTimer, setCurrentContractionTimer] =
        useState<number>(0)
    const [currentRestingTimer, setCurrentRestingTimer] = useState<number>(0)
    const [contractionRunning, setContractionRunning] = useState<boolean>(false)

    // data for each contraction
    const [startTime, setStartTime] = useState<undefined | Date>(undefined)
    const [endTime, setEndTime] = useState<undefined | Date>(undefined)

    function handleResetAll() {
        setContractionArray([])
        setStartTime(undefined)
        setEndTime(undefined)
        setContractionRunning(false)
        setCurrentContractionTimer(0)
        setCurrentRestingTimer(0)
    }

    function handleStart() {
        setStartTime(new Date())
        setContractionRunning(true)
    }

    function handleStop() {
        setEndTime(new Date())
        setContractionRunning(false)
    }

    // we need to wait for endtime to be set before we can calculate the contraction length, if we do it in the handleStop it will be undefiend the first time
    useEffect(() => {
        if (!endTime) return
        const contractionLength = calculateContractionLength(startTime, endTime)
        const frequencyLength = calculateFrequency(contractionArray, startTime)

        const newItem = {
            startTime,
            endTime,
            contractionLength,
            frequencyLength
        }
        setContractionArray([newItem, ...contractionArray])
        //eslint-disable-next-line
    }, [endTime])

    useEffect(() => {
        if (contractionRunning === false && startTime) {
            setCurrentContractionTimer(0)

            const interval = setInterval(
                () => setCurrentRestingTimer(currentRestingTimer + 1),
                10
            )
            return () => clearInterval(interval)
        }

        if (contractionRunning === true) {
            const interval = setInterval(
                () => setCurrentContractionTimer(currentContractionTimer + 1),
                10
            )
            return () => clearInterval(interval)
        }
    }, [
        startTime,
        contractionRunning,
        currentContractionTimer,
        currentRestingTimer
    ])

    return (
        <StyledWrapper>
            <StyledHeader>
                <Icon name={"pregnancyOracleClock"} />
                <Typography variant="h2">
                    {t.pregnancyOracle.rantCounter}
                </Typography>
            </StyledHeader>

            <StyledMainWrapper>
                <StyledResetButton
                    size="small"
                    onClick={() => handleResetAll()}>
                    <Typography variant="body2">
                        {" "}
                        {t.contractionCounter.reset}
                    </Typography>
                </StyledResetButton>
                <MainCounter
                    time={
                        contractionRunning
                            ? currentContractionTimer
                            : currentRestingTimer
                    }
                    title={
                        contractionRunning
                            ? `${t.contractionCounter.timeOngoingContraction}`
                            : `${t.contractionCounter.timeSinceLastContraction}`
                    }
                />
                <StyledButton
                    disableRipple
                    isrunning={contractionRunning.toString()}
                    size="large"
                    variant="contained"
                    startIcon={
                        contractionRunning ? (
                            <Icon name="stop" />
                        ) : (
                            <Icon name="start" />
                        )
                    }
                    onClick={contractionRunning ? handleStop : handleStart}>
                    {contractionRunning
                        ? `${t.contractionCounter.stop}`
                        : `${t.contractionCounter.start}`}
                </StyledButton>

                {endTime && <ResultList contractionArray={contractionArray} />}
                <Typography variant="body2">
                    {t.pregnancyOracle.backTo}{" "}
                    <StyledLink
                        href={rewrites["/pregnancyoracle"] + "/foster/0"}
                        color="inherit"
                        fontStyle="inherit">
                        {t.pregnancyOracle.title}
                    </StyledLink>
                </Typography>
            </StyledMainWrapper>
            <VisuallyHidden>
                <PrintResultList contractionArray={contractionArray} />
            </VisuallyHidden>
        </StyledWrapper>
    )
}

function MainCounter({ time, title }: { time: number; title: string }) {
    const contractionTime = formatCounterTime(time)

    return (
        <StyledMainCounterWrapper>
            {time > 0 ? (
                <Typography variant="h6">{title}</Typography>
            ) : (
                <Typography variant="h6">
                    {t.contractionCounter.pressStart}
                </Typography>
            )}
            <StyledMainCounterTypography
                variant="h1"
                fontFamily="figtree">
                {contractionTime}
            </StyledMainCounterTypography>
        </StyledMainCounterWrapper>
    )
}

function ResultList({
    contractionArray
}: {
    contractionArray: IContractionCounter[]
}) {
    return (
        <StyledResultList>
            <StyledResultGrid>
                <Typography variant="subtitle2">
                    {t.contractionCounter.startToStop}
                </Typography>
                <Typography variant="subtitle2">
                    {t.contractionCounter.length}
                </Typography>
                <Typography variant="subtitle2">
                    {t.contractionCounter.frequency}
                </Typography>
            </StyledResultGrid>
            {contractionArray.map(item => (
                <li key={item.startTime?.toString()}>
                    <StyledResultGrid>
                        <Typography variant="subtitle2">
                            {formatCurrentTime(item.startTime)} -
                            {formatCurrentTime(item.endTime)}
                        </Typography>

                        <Typography variant="subtitle2">
                            {item.contractionLength?.toString()}
                        </Typography>
                        <Typography variant="subtitle2">
                            {item.frequencyLength?.toString()}
                        </Typography>
                    </StyledResultGrid>
                    <Divider />
                </li>
            ))}
            <Tooltip
                title={t.contractionCounter.printResults}
                arrow
                placement="left">
                <StyledFab
                    size="small"
                    onClick={printResult}>
                    <Icon name="downloadOutlined" />
                </StyledFab>
            </Tooltip>
        </StyledResultList>
    )
}

// inlinestyling for the print version, styled components does not work in print
function PrintResultList({
    contractionArray
}: {
    contractionArray: IContractionCounter[]
}) {
    return (
        <StyledResultList
            id="resultlist"
            style={{
                width: "70%",
                margin: "auto",
                fontFamily: "figtree",
                listStyle: "none"
            }}>
            <Typography variant="h2">
                {t.contractionCounter.results}{" "}
            </Typography>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h5">
                    {t.contractionCounter.startToStop}
                </Typography>

                <Typography variant="h5">
                    {t.contractionCounter.length}
                </Typography>
                <Typography variant="h5">
                    {t.contractionCounter.frequency}
                </Typography>
            </div>
            {contractionArray.map(item => (
                <li key={item.startTime?.toString()}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between"
                        }}>
                        <Typography>
                            {formatCurrentTime(item.startTime)} -
                            {formatCurrentTime(item.endTime)}
                        </Typography>

                        <Typography>
                            {item.contractionLength?.toString()}
                        </Typography>
                        <Typography>
                            {item.frequencyLength?.toString()}
                        </Typography>
                    </div>
                    <Divider />
                </li>
            ))}
        </StyledResultList>
    )
}

const StyledWrapper = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.pregnancy.light,
    display: "flex",
    width: "100%",
    margin: "auto",
    flexDirection: "column"
}))

const StyledHeader = styled("div")(({ theme }) => ({
    width: "100%",
    margin: "auto",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.pregnancy.main,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "& .MuiSvgIcon-root": {
        fontSize: "2.4rem"
    },
    "& .MuiTypography-root": {
        color: theme.palette.common.black
    }
}))

const StyledMainWrapper = styled("div")(({ theme }) => ({
    padding: theme.spacing(3, 2),
    alignItems: "center",
    display: "flex",
    width: "100%",
    gap: theme.spacing(3),
    flexDirection: "column"
}))

const StyledMainCounterWrapper = styled("div")(({ theme }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5)
}))

const StyledMainCounterTypography = styled(Typography)(({ theme }) => ({
    borderRadius: "2px",
    fontSize: "2rem",
    textAlign: "center",
    width: "100%",
    backgroundColor: "white",
    padding: theme.spacing(1)
}))

const StyledResetButton = styled(Button)({
    textDecoration: "underline 1px solid black",
    textUnderlineOffset: "0.2em",
    width: "fit-content"
})

const StyledButton = styled(Button)<{ isrunning: string }>(
    ({ theme, isrunning }) => ({
        "& .MuiButton-startIcon": {
            "& .MuiSvgIcon-root": {
                fontSize: "3rem"
            }
        },
        width: "100%",
        fontSize: "2.4rem",
        color: theme.palette.common.black,
        backgroundColor:
            isrunning === "true"
                ? theme.palette.pregnancy.main
                : theme.palette.pregnancy.dark,
        border:
            isrunning === "true"
                ? `2px solid white`
                : `2px solid ${theme.palette.pregnancy.main}`,
        "&:hover": {
            border:
                isrunning === "true"
                    ? `2px solid ${theme.palette.pregnancy.main}`
                    : `2px solid ${theme.palette.pregnancy.dark}`,
            backgroundColor:
                isrunning === "true"
                    ? theme.palette.pregnancy.main
                    : theme.palette.pregnancy.dark
        },
        "&:focus": {
            outline: "none"
        }
    })
)

const StyledResultList = styled("ol")(({ theme }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    listStyle: "none",
    borderRadius: "2px",
    margin: theme.spacing(0),
    padding: theme.spacing(2),
    backgroundColor: "white"
}))

const StyledResultGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "1.8fr 1fr 1fr"
})

const StyledFab = styled(Fab)(({ theme }) => ({
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.pregnancy.main,
    marginLeft: "auto",
    boxShadow: "none",
    "&:hover": {
        backgroundColor: theme.palette.pregnancy.dark
    }
}))

const StyledLink = styled(Link)({
    textDecoration: "underline 1px solid black",
    textUnderlineOffset: "0.2em"
})

const VisuallyHidden = styled("div")({
    visibility: "hidden",
    display: "none"
})
